"use client";

import { useState } from "react";
import {
  ErrorNotice,
  Notice,
  PillGroup,
  SelectField,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import { WIDGET_H2 } from "@/components/tool-kit/styles";
import type { BUOYANCY_CALCULATOR_STRINGS } from "@/components/tools/buoyancy-calculator";
import { BuoyancyDiagram } from "@/components/tools/physics-fields/buoyancy-diagram";
import {
  carrySolved,
  DiagramFrame,
  type SolveFieldSpec,
  SolveGrid,
  WorkingLines,
} from "@/components/tools/physics-fields/shared";
import { useToolNumber } from "@/components/tools/tool-ui";
import { BODIES } from "@/lib/tool-kit/physics-fields/bodies";
import {
  type ArchimedesTarget,
  type BuoyancyError,
  type FloatState,
  solveArchimedes,
  solveFloat,
} from "@/lib/tool-kit/physics-fields/buoyancy";
import {
  archimedesWorking,
  floatWorking,
} from "@/lib/tool-kit/physics-fields/buoyancy-working";
import { FLUIDS, findFluid } from "@/lib/tool-kit/physics-fields/fluids";
import { formatSig } from "@/lib/tool-kit/physics-fields/format";
import { readQuantity } from "@/lib/tool-kit/physics-fields/input";
import {
  findMaterial,
  MATERIALS,
} from "@/lib/tool-kit/physics-fields/materials";
import {
  ACCELERATION_UNITS,
  DENSITY_UNITS,
  FORCE_UNITS,
  MASS_UNITS,
  VOLUME_UNITS,
} from "@/lib/tool-kit/physics-fields/units";

type Strings = typeof BUOYANCY_CALCULATOR_STRINGS;

interface PanelProps {
  strings: Strings;
  sig: number;
}

function Failure({
  error,
  strings,
}: {
  error: BuoyancyError;
  strings: Strings;
}) {
  const messages: Record<BuoyancyError, string> = {
    missing: strings.errorMissing,
    "non-positive": strings.errorPositive,
    "not-finite": strings.errorRange,
  };
  return error === "missing" ? (
    <Notice>{messages.missing}</Notice>
  ) : (
    <ErrorNotice>{messages[error]}</ErrorNotice>
  );
}

function FluidAndGravity({
  strings,
  fluid,
  planet,
  onFluid,
  onPlanet,
  idPrefix,
}: {
  strings: Strings;
  fluid: string;
  planet: string;
  onFluid: (id: string) => void;
  onPlanet: (id: string) => void;
  idPrefix: string;
}) {
  const fluidNames = strings.fluidNames as Record<string, string>;
  const planetNames = strings.planetNames as Record<string, string>;
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <SelectField
        id={`${idPrefix}-fluid`}
        label={strings.fluidLabel}
        onChange={onFluid}
        options={[
          { value: "custom", label: strings.custom },
          ...FLUIDS.map((f) => ({
            value: f.id,
            label: fluidNames[f.id] ?? f.id,
          })),
        ]}
        value={fluid}
      />
      <SelectField
        id={`${idPrefix}-planet`}
        label={strings.planetLabel}
        onChange={onPlanet}
        options={[
          { value: "custom", label: strings.custom },
          ...BODIES.map((b) => ({
            value: b.id,
            label: planetNames[b.id] ?? b.id,
          })),
        ]}
        value={planet}
      />
    </div>
  );
}

function verdictText(state: FloatState, strings: Strings): string {
  const labels: Record<FloatState, string> = {
    floats: strings.floats,
    sinks: strings.sinks,
    neutral: strings.neutral,
  };
  return labels[state];
}

/* ── Will it float? ───────────────────────────────────────────────────────── */

type FloatKey = "o" | "m" | "V" | "f" | "g";
type Describe = "density" | "mass";

export function FloatPanel({ strings, sig }: PanelProps) {
  const fmt = useToolNumber();
  const [describe, setDescribe] = useState<Describe>("density");
  const [material, setMaterial] = useState("pine");
  const [fluid, setFluid] = useState("fresh-water");
  const [planet, setPlanet] = useState("earth");
  const [text, setText] = useState<Record<FloatKey, string>>({
    o: "500",
    m: "10",
    V: "20",
    f: "998.2",
    g: "9.80665",
  });
  const [unit, setUnit] = useState<Record<FloatKey, string>>({
    o: "kg/m3",
    m: "kg",
    V: "L",
    f: "kg/m3",
    g: "m/s2",
  });

  const outcome = solveFloat({
    objectDensity:
      describe === "density"
        ? readQuantity(text.o, DENSITY_UNITS, unit.o)
        : null,
    mass: describe === "mass" ? readQuantity(text.m, MASS_UNITS, unit.m) : null,
    volume: readQuantity(text.V, VOLUME_UNITS, unit.V),
    fluidDensity: readQuantity(text.f, DENSITY_UNITS, unit.f),
    gravity: readQuantity(text.g, ACCELERATION_UNITS, unit.g),
  });
  const value = outcome.ok ? outcome.value : null;
  const specs: SolveFieldSpec<FloatKey>[] = [
    describe === "density"
      ? {
          key: "o",
          label: strings.objectDensity,
          units: DENSITY_UNITS,
          solved: false,
        }
      : {
          key: "m",
          label: strings.objectMass,
          units: MASS_UNITS,
          solved: false,
        },
    {
      key: "V",
      label: strings.objectVolume,
      units: VOLUME_UNITS,
      solved: false,
    },
    {
      key: "f",
      label: strings.fluidDensity,
      units: DENSITY_UNITS,
      solved: false,
    },
    {
      key: "g",
      label: strings.gravity,
      units: ACCELERATION_UNITS,
      solved: false,
    },
  ];
  const materialNames = strings.materialNames as Record<string, string>;
  const pickMaterial = (id: string) => {
    setMaterial(id);
    const m = findMaterial(id);
    if (m) {
      setText((c) => ({ ...c, o: String(m.density) }));
      setUnit((c) => ({ ...c, o: "kg/m3" }));
      setDescribe("density");
    }
  };
  const pickFluid = (id: string) => {
    setFluid(id);
    const f = findFluid(id);
    if (f) {
      setText((c) => ({ ...c, f: String(f.density) }));
      setUnit((c) => ({ ...c, f: "kg/m3" }));
    }
  };
  const pickPlanet = (id: string) => {
    setPlanet(id);
    const b = BODIES.find((body) => body.id === id);
    if (b) {
      setText((c) => ({ ...c, g: String(b.gravity) }));
      setUnit((c) => ({ ...c, g: "m/s2" }));
    }
  };
  const net = value === null ? 0 : value.apparentWeightSubmerged;

  return (
    <>
      <SelectField
        id="bc-material"
        label={strings.materialLabel}
        onChange={pickMaterial}
        options={[
          { value: "custom", label: strings.custom },
          ...MATERIALS.map((m) => ({
            value: m.id,
            label: materialNames[m.id] ?? m.id,
          })),
        ]}
        value={material}
      />
      <PillGroup
        label={strings.describeBy}
        name="bc-describe"
        onChange={(next: Describe) => {
          setDescribe(next);
          if (next === "mass") {
            setMaterial("custom");
          }
        }}
        options={[
          { value: "density", label: strings.byDensity },
          { value: "mass", label: strings.byMass },
        ]}
        value={describe}
      />
      <FluidAndGravity
        fluid={fluid}
        idPrefix="bc-float"
        onFluid={pickFluid}
        onPlanet={pickPlanet}
        planet={planet}
        strings={strings}
      />
      <SolveGrid
        fmt={fmt}
        idPrefix="bc-f"
        onText={(key, v) => {
          if (key === "o") {
            setMaterial("custom");
          }
          if (key === "f") {
            setFluid("custom");
          }
          if (key === "g") {
            setPlanet("custom");
          }
          setText({ ...text, [key]: v });
        }}
        onUnit={(key, v) => setUnit({ ...unit, [key]: v })}
        sig={sig}
        solvedSI={{}}
        specs={specs}
        text={text}
        unit={unit}
        unitTemplate={strings.unitFor}
      />
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {value === null ? (
          <Failure
            error={outcome.ok ? "missing" : outcome.error}
            strings={strings}
          />
        ) : (
          <>
            <StatGrid>
              <StatCard
                emphasis
                label={strings.verdict}
                value={verdictText(value.state, strings)}
              />
              <StatCard
                label={strings.fractionSubmerged}
                value={`${formatSig(fmt, value.fractionSubmerged * 100, sig)}%`}
              />
              <StatCard
                label={strings.objectDensity}
                value={`${formatSig(fmt, value.objectDensity, sig)} kg/m³`}
              />
              <StatCard
                label={strings.weight}
                value={`${formatSig(fmt, value.weight, sig)} N`}
              />
              <StatCard
                label={strings.buoyantForce}
                value={`${formatSig(fmt, value.buoyantForce, sig)} N`}
              />
              <StatCard
                label={net > 0 ? strings.apparentWeight : strings.holdDown}
                value={`${formatSig(fmt, Math.abs(net), sig)} N`}
              />
              <StatCard
                label={strings.reserve}
                value={`${formatSig(fmt, value.reserve, sig)} N`}
              />
              <StatCard
                label={strings.submergedVolume}
                value={`${formatSig(fmt, value.submergedVolume, sig)} m³`}
              />
            </StatGrid>
            <WorkingLines
              heading={strings.working}
              lines={floatWorking(value, describe === "mass", fmt, sig)}
            />
            <DiagramFrame caption={strings.floatCaption}>
              <BuoyancyDiagram
                description={strings.diagramDescription}
                forceRatio={value.buoyantForce / value.weight}
                fraction={value.fractionSubmerged}
                id="bc-float-diagram"
                label={strings.diagramLabel}
                sinks={value.state === "sinks"}
              />
            </DiagramFrame>
          </>
        )}
      </section>
    </>
  );
}

/* ── F = ρ V g ────────────────────────────────────────────────────────────── */

type ForceKey = "F" | "V" | "d" | "g";

export function ForcePanel({ strings, sig }: PanelProps) {
  const fmt = useToolNumber();
  const [target, setTarget] = useState<ArchimedesTarget>("force");
  const [fluid, setFluid] = useState("fresh-water");
  const [planet, setPlanet] = useState("earth");
  const [text, setText] = useState<Record<ForceKey, string>>({
    F: "",
    V: "1",
    d: "998.2",
    g: "9.80665",
  });
  const [unit, setUnit] = useState<Record<ForceKey, string>>({
    F: "kN",
    V: "m3",
    d: "kg/m3",
    g: "m/s2",
  });
  const outcome = solveArchimedes(
    {
      force: readQuantity(text.F, FORCE_UNITS, unit.F),
      volume: readQuantity(text.V, VOLUME_UNITS, unit.V),
      density: readQuantity(text.d, DENSITY_UNITS, unit.d),
      gravity: readQuantity(text.g, ACCELERATION_UNITS, unit.g),
    },
    target
  );
  const value = outcome.ok ? outcome.value : null;
  const specs: SolveFieldSpec<ForceKey>[] = [
    {
      key: "F",
      label: strings.buoyantForceBox,
      units: FORCE_UNITS,
      solved: target === "force",
    },
    {
      key: "V",
      label: strings.displacedVolume,
      units: VOLUME_UNITS,
      solved: target === "volume",
      hint: strings.displacedHint,
    },
    {
      key: "d",
      label: strings.fluidDensity,
      units: DENSITY_UNITS,
      solved: target === "density",
    },
    {
      key: "g",
      label: strings.gravity,
      units: ACCELERATION_UNITS,
      solved: false,
    },
  ];
  const solvedSI = {
    F: value?.force ?? null,
    V: value?.volume ?? null,
    d: value?.density ?? null,
  };
  const pickFluid = (id: string) => {
    setFluid(id);
    const f = findFluid(id);
    if (f) {
      setText((c) => ({ ...c, d: String(f.density) }));
      setUnit((c) => ({ ...c, d: "kg/m3" }));
    }
  };
  const pickPlanet = (id: string) => {
    setPlanet(id);
    const b = BODIES.find((body) => body.id === id);
    if (b) {
      setText((c) => ({ ...c, g: String(b.gravity) }));
      setUnit((c) => ({ ...c, g: "m/s2" }));
    }
  };

  return (
    <>
      <FluidAndGravity
        fluid={fluid}
        idPrefix="bc-force"
        onFluid={pickFluid}
        onPlanet={pickPlanet}
        planet={planet}
        strings={strings}
      />
      <PillGroup
        label={strings.solveFor}
        name="bc-solve"
        onChange={(next: ArchimedesTarget) => {
          setText(carrySolved(specs, text, unit, solvedSI, sig));
          setTarget(next);
        }}
        options={[
          { value: "force", label: strings.forceShort },
          { value: "volume", label: strings.volumeShort },
          { value: "density", label: strings.densityShort },
        ]}
        value={target}
      />
      <SolveGrid
        fmt={fmt}
        idPrefix="bc-a"
        onText={(key, v) => {
          if (key === "d") {
            setFluid("custom");
          }
          if (key === "g") {
            setPlanet("custom");
          }
          setText({ ...text, [key]: v });
        }}
        onUnit={(key, v) => setUnit({ ...unit, [key]: v })}
        sig={sig}
        solvedSI={solvedSI}
        specs={specs}
        text={text}
        unit={unit}
        unitTemplate={strings.unitFor}
      />
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {value === null ? (
          <Failure
            error={outcome.ok ? "missing" : outcome.error}
            strings={strings}
          />
        ) : (
          <>
            <StatGrid>
              <StatCard
                emphasis
                label={strings.buoyantForceBox}
                value={`${formatSig(fmt, value.force, sig)} N`}
              />
              <StatCard
                label={strings.displacedMass}
                value={`${formatSig(fmt, value.displacedMass, sig)} kg`}
              />
              <StatCard
                label={strings.displacedVolume}
                value={`${formatSig(fmt, value.volume, sig)} m³`}
              />
            </StatGrid>
            <WorkingLines
              heading={strings.working}
              lines={archimedesWorking(target, value, fmt, sig)}
            />
            <DiagramFrame caption={strings.forceCaption}>
              <BuoyancyDiagram
                description={strings.diagramDescription}
                forceRatio={1}
                fraction={1}
                id="bc-force-diagram"
                label={strings.diagramLabel}
                sinks={false}
              />
            </DiagramFrame>
          </>
        )}
      </section>
    </>
  );
}
